import { z } from 'zod';

/**
 * Response envelope and pagination (doc 05 section 3).
 *
 * Every list response has the same shape. Cursors are opaque to the client by
 * contract -- documented as such -- so we can change the encoding without a
 * major version bump.
 */

export const CursorSchema = z.string().min(1).max(512);

export const PaginationSchema = z.object({
  next_cursor: z.string().nullable(),
  has_more: z.boolean(),
  limit: z.number().int(),
  total: z.number().int().optional(),
});
export type Pagination = z.infer<typeof PaginationSchema>;

export function listResponse<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: z.array(item), pagination: PaginationSchema });
}

/**
 * A boolean from a string, that actually means what it says.
 *
 * `z.coerce.boolean()` is `Boolean(value)`, and every non-empty string is
 * truthy -- so it parses "false", "off" and "0" as TRUE. Only the empty string
 * is false. That is not a subtlety; it inverts the flag:
 *
 *     ARC_API_KEYS_ENABLED=false   ->  true   (the API-key door, opened)
 *     ARC_SCHEDULER=false          ->  true   (jobs run where they must not)
 *     ?answered=false              ->  true   (unanswered calls, filtered out)
 *     ?failed_only=false           ->  true   (only failures, when asked for all)
 *
 * All six uses in this codebase were wrong in that direction. This accepts the
 * spellings people actually write and REFUSES anything else, so a typo is a
 * loud parse error rather than a silent inversion. Booleans that arrive as real
 * booleans (a JSON body) pass through untouched.
 */
export const BoolSchema = z.union([
  z.boolean(),
  z.enum(['true', 'on', 'yes', '1']).transform(() => true),
  z.enum(['false', 'off', 'no', '0', '']).transform(() => false),
]);

/** Default 50, max 200. CDR export raises the ceiling; see doc 05 section 3. */
export const ListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  cursor: CursorSchema.optional(),
  include_total: BoolSchema.default(false),
});
export type ListQuery = z.infer<typeof ListQuerySchema>;

/**
 * Money is a STRING everywhere it crosses a boundary.
 *
 * Postgres numeric(18,6) does not fit in a JS number without loss, and the loss
 * is silent. Parsing to a float for display is the specific bug this prevents
 * (doc 07 section 3). Format at the edge, from the string.
 */
export const MoneySchema = z.string().regex(/^-?\d+(\.\d{1,6})?$/);

export const E164Schema = z.string().regex(/^\+[1-9]\d{6,14}$/);
export const SlugSchema = z.string().regex(/^[a-z0-9]([a-z0-9-]{0,60}[a-z0-9])?$/);
export const SipDomainSchema = z.string().regex(/^[a-z0-9]([a-z0-9.-]{0,251}[a-z0-9])?$/);

/**
 * A seat's SIP username: its extension number and its account, `1001-meridian`.
 *
 * A bare `1001` is the first thing every SIP scanner tries, and every account
 * has one. Scoping the username to the account means a guess has to know which
 * customer it is aiming at, and lets the edge refuse anything shaped otherwise
 * before it reaches the switch (docs/26 E8).
 *
 * The account part is the first label of the SIP domain, not the slug. The
 * domain is permanent (arc.forbid_sip_domain_change) and a slug can be renamed,
 * and a username that changes is a phone that stops registering. The domain is
 * also what OpenSIPS can check it against: the domain the phone signs in to.
 */
export function sipUsernameFor(number: string, sipDomain: string): string {
  return `${number}-${sipDomain.split('.')[0]}`;
}

/**
 * The extension number a SIP username signs in as: the bare number, or the
 * account-scoped username for this domain. Null for anything else -- a device
 * identity, another account's username, or junk.
 */
export function seatNumberFromSipUser(user: string, sipDomain: string): string | null {
  if (/^\d+$/.test(user)) return user;
  const suffix = `-${sipDomain.split('.')[0]}`;
  if (!user.endsWith(suffix)) return null;
  const number = user.slice(0, -suffix.length);
  return /^\d+$/.test(number) ? number : null;
}

/** ISO 8601 with an explicit offset. Never a bare local timestamp. */
export const TimestampSchema = z.string().datetime({ offset: true });

export const IdSchema = z.string().uuid();

/** Sort spec: comma list, '-' prefix for descending. */
export const SortSchema = z.string().regex(/^-?[a-z_]+(,-?[a-z_]+)*$/);
